import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { refreshOutline } from 'ionicons/icons';
import { ApiLoadErrorComponent } from '../api-load-error/api-load-error.component';
import { AnalyticsApiError, AnalyticsApiService, AnalyticsEventRecord, AnalyticsVisitorSummary } from '../../services/analytics-api.service';

@Component({
  selector: 'app-website-analytics-panel',
  standalone: true,
  imports: [DatePipe, FormsModule, IonIcon, ApiLoadErrorComponent],
  templateUrl: './website-analytics-panel.component.html',
  styleUrls: ['./website-analytics-panel.component.scss']
})
export class WebsiteAnalyticsPanelComponent implements OnChanges {
  @Input({ required: true }) websitePublicId = '';

  isLoading = false;
  isLoadingDetail = false;
  loadError = '';
  detailError = '';
  visitors: AnalyticsVisitorSummary[] = [];
  selectedVisitor: AnalyticsVisitorSummary | null = null;
  selectedEvents: AnalyticsEventRecord[] = [];
  filters = { search: '', sort: 'recent' };

  constructor(private readonly analyticsApi: AnalyticsApiService) {
    addIcons({ refreshOutline });
  }

  ngOnChanges(): void {
    if (this.websitePublicId) {
      this.loadVisitors();
    }
  }

  get filteredVisitors(): AnalyticsVisitorSummary[] {
    const search = this.filters.search.trim().toLowerCase();
    const visitors = search ? this.visitors.filter((visitor) => visitor.visitorId.toLowerCase().includes(search)) : [...this.visitors];
    return visitors.sort((left, right) => this.filters.sort === 'active'
      ? (right.clicks + right.focuses + right.scrolls + right.formSubmits + right.outboundActions + right.mediaInteractions + right.contentViews)
        - (left.clicks + left.focuses + left.scrolls + left.formSubmits + left.outboundActions + left.mediaInteractions + left.contentViews)
      : right.lastActivityAt.localeCompare(left.lastActivityAt));
  }

  loadVisitors(): void {
    this.isLoading = true;
    this.loadError = '';
    this.analyticsApi.getWebsiteVisitors(this.websitePublicId).subscribe({
      next: (visitors) => { this.visitors = visitors; this.isLoading = false; },
      error: (error: AnalyticsApiError) => { this.loadError = error.message; this.isLoading = false; }
    });
  }

  openVisitor(visitor: AnalyticsVisitorSummary): void {
    if (this.selectedVisitor?.visitorId === visitor.visitorId && this.selectedEvents.length) {
      return;
    }
    this.selectedVisitor = visitor;
    this.selectedEvents = [];
    this.detailError = '';
    this.isLoadingDetail = true;
    this.analyticsApi.getWebsiteVisitor(this.websitePublicId, visitor.visitorId).subscribe({
      next: (events) => { this.selectedEvents = events; this.isLoadingDetail = false; },
      error: (error: AnalyticsApiError) => { this.detailError = error.message; this.isLoadingDetail = false; }
    });
  }

  visitorLabel(visitorId: string): string {
    return `Visitor ${visitorId.slice(0, 8)}`;
  }

  formatDuration(value: number): string {
    const seconds = Math.max(0, Math.round(value / 1000));
    return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
  }

  eventLabel(event: AnalyticsEventRecord): string {
    if (event.type === 'page_duration') { return `Active for ${this.formatDuration(event.durationMs ?? 0)}`; }
    if (event.type === 'scroll') { return `Scrolled to ${event.scrollPercent ?? 0}%`; }
    if (event.type === 'form_start') { return `Started ${event.elementLabel || 'a form'}`; }
    if (event.type === 'form_submit') { return `Submitted ${event.elementLabel || 'a form'}`; }
    if (event.type === 'outbound_link') { return `Opened external link: ${event.elementLabel || 'link'}`; }
    if (event.type === 'download') { return `Downloaded: ${event.elementLabel || 'file'}`; }
    if (event.type === 'content_view') { return `Viewed section: ${event.elementLabel || 'section'}`; }
    if (event.type === 'media_play') { return `Started media: ${event.elementLabel || 'media'}`; }
    if (event.type === 'media_pause') { return `Paused media: ${event.elementLabel || 'media'}`; }
    if (event.type === 'media_complete') { return `Completed media: ${event.elementLabel || 'media'}`; }
    return event.elementLabel || event.elementKind || event.type.replace('_', ' ');
  }
}
